import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthResponse, LoginInput, RegisterInput, User } from '@omnia/shared';
import { saveLayoutNow } from '../desktop/layoutSync.ts';
import { api, RequestError, setSessionToken, shouldRetry } from './api.ts';

const SESSION_KEY = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async ({ signal }) => {
      try {
        return (await api<{ user: User }>('/api/auth/me', { signal })).user;
      } catch (error) {
        if (error instanceof RequestError && error.status === 401) return null;
        throw error;
      }
    },
    retry: shouldRetry,
    staleTime: Infinity,
  });
}

export function useSignIn(mode: 'login' | 'register') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput | RegisterInput) =>
      api<AuthResponse>(`/api/auth/${mode}`, { method: 'POST', body: input }),
    onSuccess: ({ user, token }) => {
      setSessionToken(token);
      client.setQueryData(SESSION_KEY, user);
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // The session is about to end, so the last window move has to be stored while it still works.
      await saveLayoutNow();
      await api<void>('/api/auth/logout', { method: 'POST' });
    },
    // Sign out locally even if the server is unreachable; the session expires on its own.
    onSettled: () => {
      setSessionToken(null);
      // Signed-out state first, then drop everything else: clear() would detach the mounted session observer
      // from its query, and the shell would keep showing the previous user.
      client.setQueryData(SESSION_KEY, null);
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY[0] });
    },
  });
}
